import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { BsDownload, BsThreeDotsVertical } from "react-icons/bs";
import { HiOutlineTrash, HiOutlineEye, HiPencil, HiOutlineFolder } from "react-icons/hi";
import { MdDriveFileRenameOutline } from "react-icons/md";
import { RiGitRepositoryPrivateLine } from "react-icons/ri";
import {
  canAccessDocumentAction,
  canAccessFrontlineMyDocumentsFolderAction,
  DOCUMENT_PERMISSION_ACTIONS,
  isFrontlineEmployeeUser,
  roleAccess,
  usePermissionsVersion,
} from "helpers/RolesPermission";
import { USER_ROLE } from "Constants/constant";
import { toast } from "react-toastify";

export const PortalDropDown = (props) => {
  const {
    type,
    clickHandler,
    permissionRename,
    role,
    isUserGracePeriodOver,
    onMenuStateChange,
    enableMoveToFolder = false,
    /** When true (e.g. Required Documents folder), hide Remove/Rename on folders. */
    isLockedFolder = false,
    triggerClassName,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, openUpward: false, alignLeft: false });

  usePermissionsVersion();

  // Build menu list based on permissions and type
  const menuList = [];

  /** Black portal participant documents: files view/download only; no folder Remove. */
  const isBlackPortalParticipantDocuments =
    role === "participant" && roleAccess([USER_ROLE.Sp_Standard_User]);

  /** Primary portal only — hide file Remove on black portal (worker + participant). */
  const isBlackPortalFileMenu =
    isBlackPortalParticipantDocuments ||
    (role === "hrm" && isFrontlineEmployeeUser());

  const isFrontlineMyDocumentsFolderMenu =
    role === "hrm" && isFrontlineEmployeeUser() && type === "folder";

  const canFolderDocumentAction = (action) => {
    if (isFrontlineMyDocumentsFolderMenu) {
      return (
        canAccessFrontlineMyDocumentsFolderAction(action) ||
        canAccessDocumentAction(role, action)
      );
    }
    return canAccessDocumentAction(role, action);
  };

  const removeItem = {
    label: "Remove",
    icon: <HiOutlineTrash className='h-4 w-4' />,
  };

  if (
    roleAccess([
      USER_ROLE.Sp_Admin,
      USER_ROLE.Sp_manager,
      USER_ROLE.Sp_Standard_User,
    ])
  ) {
    if (
      role === "participant" &&
      type === "file" &&
      !roleAccess([USER_ROLE.Sp_Standard_User])
    ) {
      menuList.unshift({
        label: "Change Permission",
        icon: <RiGitRepositoryPrivateLine className='h-4 w-4' />,
      });
    }
  }

  if (type === "file") {
    const download = {
      label: "Download",
      icon: <BsDownload className='h-4 w-4' />,
    };
    menuList.unshift(download);

    const view = {
      label: "View",
      icon: <HiOutlineEye className='h-4 w-4' />,
    };
    menuList.unshift(view);

    if (
      !isBlackPortalParticipantDocuments &&
      canAccessDocumentAction(role, DOCUMENT_PERMISSION_ACTIONS.EDIT)
    ) {
      menuList.unshift({
        label: "Edit",
        icon: <HiPencil className='h-4 w-4' />,
      });
    }

    if (!isBlackPortalParticipantDocuments && enableMoveToFolder) {
      menuList.unshift({
        label: "Move to folder",
        icon: <HiOutlineFolder className='h-4 w-4' />,
      });
    }

    const canRemoveFile =
      !isBlackPortalFileMenu &&
      canAccessDocumentAction(role, DOCUMENT_PERMISSION_ACTIONS.DELETE);
    if (canRemoveFile) {
      menuList.push(removeItem);
    }
  } else if (type === "folder" && !isLockedFolder) {
    const canRenameFolder =
      !permissionRename &&
      canFolderDocumentAction(DOCUMENT_PERMISSION_ACTIONS.FOLDER_UPDATE);
    const canRemoveFolder = canFolderDocumentAction(
      DOCUMENT_PERMISSION_ACTIONS.FOLDER_DELETE
    );
    const canMoveFolderMenu =
      !isBlackPortalParticipantDocuments &&
      (enableMoveToFolder || canRenameFolder);

    // Folder menu order: Move → Rename → Remove.
    if (canMoveFolderMenu) {
      menuList.push({
        label: "Move to folder",
        icon: <HiOutlineFolder className='h-4 w-4' />,
      });
    }
    if (canRenameFolder) {
      menuList.push({
        label: "Rename",
        icon: <MdDriveFileRenameOutline className='h-4 w-4' />,
      });
    }
    if (canRemoveFolder && !isBlackPortalParticipantDocuments) {
      menuList.push(removeItem);
    }
  } else if (isBlackPortalParticipantDocuments && type !== "file") {
    menuList.push({
      label: "Open",
      icon: <HiOutlineFolder className='h-4 w-4' />,
    });
  }

  const menuItemCount = menuList.length;

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const menuHeight = menuItemCount * 40 + 16;
    const menuWidth = 224;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const spaceOnRight = viewportWidth - rect.right;
    const spaceOnLeft = rect.left;

    const openUpward = spaceBelow < menuHeight && spaceAbove > menuHeight;
    const alignLeft = spaceOnRight < menuWidth && spaceOnLeft >= menuWidth;

    setPosition({
      top: openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4,
      left: alignLeft ? rect.right - menuWidth : rect.left,
      openUpward,
      alignLeft,
    });
  }, [menuItemCount]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    if (onMenuStateChange) {
      onMenuStateChange(false);
    }
  }, [onMenuStateChange]);

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleScroll = () => {
      closeMenu();
    };
    const handleResize = () => updatePosition();

    // Scroll does not bubble; capture on document catches nested overflow scrollers too.
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, closeMenu, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        !event.target.closest('.portal-dropdown-menu')
      ) {
        setIsOpen(false);
        if (onMenuStateChange) {
          onMenuStateChange(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onMenuStateChange]);

  const handleToggle = (e) => {
    e.stopPropagation();
    const newState = !isOpen;
    setIsOpen(newState);
    if (onMenuStateChange) {
      onMenuStateChange(newState);
    }
  };

  const handleClick = (e, label) => {
    e.stopPropagation();
    
    // Close menu first
    setIsOpen(false);
    if (onMenuStateChange) {
      onMenuStateChange(false);
    }

    if (label === "Move to folder") {
      const canMove =
        enableMoveToFolder ||
        (type === "folder" &&
          !isLockedFolder &&
          canFolderDocumentAction(DOCUMENT_PERMISSION_ACTIONS.FOLDER_UPDATE));
      if (canMove) {
        clickHandler(label);
        return;
      }
      toast("Don't have permission!");
      return;
    }

    if (label === "Download" || label === "View" || label === "Open") {
      clickHandler(label);
      return;
    }

    if (type === "file") {
      if (label === "Change Permission") {
        clickHandler(label);
        return;
      }

      if (
        canAccessDocumentAction(role, DOCUMENT_PERMISSION_ACTIONS.DELETE) &&
        label === "Remove"
      ) {
        if (isUserGracePeriodOver) return;
        clickHandler(label);
      } else if (
        canAccessDocumentAction(role, DOCUMENT_PERMISSION_ACTIONS.EDIT) &&
        label === "Edit"
      ) {
        clickHandler(label);
      } else if (label === "Remove" || label === "Edit") {
        toast("Don't have permission!");
      }
    } else if (type !== "file") {
      if (
        canFolderDocumentAction(DOCUMENT_PERMISSION_ACTIONS.FOLDER_UPDATE) &&
        label === "Rename"
      ) {
        clickHandler(label);
      } else if (
        canFolderDocumentAction(DOCUMENT_PERMISSION_ACTIONS.FOLDER_DELETE) &&
        label === "Remove"
      ) {
        if (isUserGracePeriodOver) return;
        clickHandler(label);
      } else {
        toast("Don't have permission!");
      }
    }
  };

  if (menuList.length === 0) {
    return null;
  }

  return (
    <div className='relative'>
      <button
        ref={buttonRef}
        type='button'
        onClick={handleToggle}
        aria-label='More actions'
        className={
          triggerClassName ||
          "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-700 hover:border-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30"
        }
      >
        <BsThreeDotsVertical className='h-3.5 w-3.5' aria-hidden />
      </button>

      {typeof window !== 'undefined' && isOpen && createPortal(
        <div
          className='portal-dropdown-menu fixed w-56 bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-[99999]'
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
        >
          <div className='px-1 py-1'>
            {menuList.map((item, index) => (
              <button
                key={index}
                type='button'
                onClick={(e) => handleClick(e, item.label)}
                disabled={isUserGracePeriodOver && item.label === "Remove"}
                className={`
                  group flex w-full items-center rounded-md px-3 py-2 text-sm
                  transition-colors duration-150
                  ${
                    isUserGracePeriodOver && item.label === "Remove"
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                  }
                `}
              >
                <span className='mr-3 flex-shrink-0'>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PortalDropDown;
